import React, { useState, useEffect } from 'react';
import Toast from './Toast';
import ImageUploadZone from './ImageUploadZone';

export default function CaseStudyEditor({ caseStudyId, onBack }) {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    client: '',
    year: '',
    thumbnail: '',
    description: '',
    tags: [],
    logos: [],
    gallery: [],
    backgroundColor: '#ffffff',
    status: 'draft',
    featured: false
  });

  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(!!caseStudyId);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (caseStudyId) {
      fetchCaseStudy();
    }
  }, [caseStudyId]);

  const fetchCaseStudy = async () => {
    try {
      const response = await fetch(`/api/case-studies/${caseStudyId}`);
      const data = await response.json();
      setFormData(data);
      setLoading(false);
    } catch (err) {
      showToast('Error loading case study', 'error');
      setLoading(false);
    }
  };

  const showToast = (msg, type = 'default') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleAddImage = (imageUrl, type) => {
    if (!imageUrl.trim()) return;

    if (type === 'thumbnail') {
      setFormData(prev => ({ ...prev, thumbnail: imageUrl }));
    } else if (type === 'logo') {
      if (!formData.logos.includes(imageUrl.trim())) {
        setFormData(prev => ({
          ...prev,
          logos: [...prev.logos, imageUrl.trim()]
        }));
      }
    } else if (type === 'gallery') {
      if (!formData.gallery.includes(imageUrl.trim())) {
        setFormData(prev => ({
          ...prev,
          gallery: [...prev.gallery, imageUrl.trim()]
        }));
      }
    }
  };

  const handleRemoveImage = (image, type) => {
    if (type === 'thumbnail') {
      setFormData(prev => ({ ...prev, thumbnail: '' }));
    } else if (type === 'logo') {
      setFormData(prev => ({
        ...prev,
        logos: prev.logos.filter(l => l !== image)
      }));
    } else if (type === 'gallery') {
      setFormData(prev => ({
        ...prev,
        gallery: prev.gallery.filter(g => g !== image)
      }));
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.client) {
      showToast('Title and client are required', 'error');
      return;
    }

    try {
      const url = caseStudyId
        ? `/api/case-studies/${caseStudyId}`
        : '/api/case-studies';

      const method = caseStudyId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Save failed');

      showToast(caseStudyId ? 'Case study updated' : 'Case study created');
      setTimeout(() => onBack(), 1500);
    } catch (err) {
      showToast('Error saving case study', 'error');
    }
  };

  if (loading) {
    return <div className="admin-page"><p>Loading...</p></div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent)',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '12px'
            }}
          >
            ← Back to case studies
          </button>
          <h1 className="admin-page__title">{caseStudyId ? 'Edit' : 'New'} <em>· case study</em></h1>
        </div>
        <div className="admin-page__actions">
          <button className="btn-ghost" onClick={onBack}>Cancel</button>
          <button className="btn-primary" onClick={handleSave}>Save case study</button>
        </div>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Basic Info */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Basic information</h3>

          <label className="field" style={{ marginBottom: '16px' }}>
            <span className="field__label">Title *</span>
            <input
              className="field__input"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g. Base App - Coinbase"
            />
          </label>

          <label className="field" style={{ marginBottom: '16px' }}>
            <span className="field__label">Subtitle</span>
            <input
              className="field__input"
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              placeholder="Brief tagline or overview"
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
            <label className="field">
              <span className="field__label">Client *</span>
              <input
                className="field__input"
                type="text"
                name="client"
                value={formData.client}
                onChange={handleChange}
                placeholder="Client name"
              />
            </label>

            <label className="field">
              <span className="field__label">Year</span>
              <input
                className="field__input"
                type="text"
                name="year"
                value={formData.year}
                onChange={handleChange}
                placeholder="2024–26"
              />
            </label>
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Description</h3>

          <label className="field">
            <span className="field__label">Full description</span>
            <textarea
              className="field__input"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="8"
              placeholder="Detailed case study description, outcomes, and impact..."
            />
          </label>

          <label className="field" style={{ marginTop: '24px' }}>
            <span className="field__label">Tile background color</span>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                className="field__input"
                type="text"
                name="backgroundColor"
                value={formData.backgroundColor}
                onChange={handleChange}
                placeholder="#ffffff"
                style={{ flex: 1 }}
              />
              <input
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                style={{
                  width: '50px',
                  height: '40px',
                  border: '1px solid rgba(244,241,234,0.1)',
                  borderRadius: '2px',
                  cursor: 'pointer'
                }}
              />
            </div>
          </label>
        </div>

        {/* Tags */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Tags</h3>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input
              className="field__input"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              placeholder="Add a tag and press Enter"
              style={{ flex: 1 }}
            />
            <button
              onClick={handleAddTag}
              className="btn-primary"
              style={{ padding: '12px 24px' }}
            >
              Add
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {formData.tags.map(tag => (
              <div
                key={tag}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  backgroundColor: 'rgba(244,241,234,0.1)',
                  border: '1px solid rgba(244,241,234,0.2)',
                  borderRadius: '2px',
                  fontSize: '13px'
                }}
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(244,241,234,0.6)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: '0'
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Thumbnail */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Thumbnail</h3>

          {formData.thumbnail && (
            <div style={{ marginBottom: '16px' }}>
              <img
                src={formData.thumbnail}
                alt="Thumbnail"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '2px',
                  marginBottom: '12px'
                }}
              />
              <button
                onClick={() => handleRemoveImage(formData.thumbnail, 'thumbnail')}
                className="btn-ghost"
                style={{ color: '#ff6b6b' }}
              >
                Remove thumbnail
              </button>
            </div>
          )}

          <ImageUploadZone onImageAdded={handleAddImage} type="thumbnail" />
        </div>

        {/* Logos */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Logos <span style={{ fontWeight: 400, fontSize: '12px', color: 'rgba(244,241,234,0.5)', textTransform: 'none' }}>— first logo displayed in tile</span></h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
            {formData.logos.map(logo => (
              <div key={logo} style={{ position: 'relative' }}>
                <img
                  src={logo}
                  alt="Logo"
                  style={{
                    width: '100%',
                    height: '100px',
                    objectFit: 'contain',
                    backgroundColor: 'rgba(244,241,234,0.05)',
                    borderRadius: '2px',
                    border: '1px solid rgba(244,241,234,0.1)'
                  }}
                />
                <button
                  onClick={() => handleRemoveImage(logo, 'logo')}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    background: 'rgba(0,0,0,0.8)',
                    border: 'none',
                    color: '#ff6b6b',
                    cursor: 'pointer',
                    width: '24px',
                    height: '24px',
                    borderRadius: '2px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <ImageUploadZone onImageAdded={handleAddImage} type="logo" />
        </div>

        {/* Gallery */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Gallery</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
            {formData.gallery.map(image => (
              <div key={image} style={{ position: 'relative' }}>
                <img
                  src={image}
                  alt="Gallery"
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '2px',
                    border: '1px solid rgba(244,241,234,0.1)'
                  }}
                />
                <button
                  onClick={() => handleRemoveImage(image, 'gallery')}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    background: 'rgba(0,0,0,0.8)',
                    border: 'none',
                    color: '#ff6b6b',
                    cursor: 'pointer',
                    width: '24px',
                    height: '24px',
                    borderRadius: '2px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <ImageUploadZone onImageAdded={handleAddImage} type="gallery" />
        </div>

        {/* Status */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Publishing</h3>

          <label className="field" style={{ marginBottom: '16px' }}>
            <span className="field__label">Status</span>
            <select
              className="field__input"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="featured">Featured</option>
            </select>
          </label>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="checkbox"
              name="featured"
              checked={formData.featured}
              onChange={handleChange}
            />
            <span style={{ fontSize: '14px' }}>Featured on landing page</span>
          </label>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}
